import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchUserData } from '../../../../Utilities/api';
import { BackButton } from '../../../../Utilities/components/Buttons';
import './UserInfo.css';

/** ES6-safe value fallback */
function uiVal(v, fallback) {
  if (v === null || v === undefined || v === '') {
    return (typeof fallback !== 'undefined' ? fallback : 'N/A');
  }
  return v;
}

/** ES6-safe date prettifier */
function uiPrettyDate(iso) {
  if (!iso) return 'N/A';
  try { return new Date(iso).toLocaleDateString(); } catch (e) { return 'Invalid Date'; }
}

/** ES6-safe phone formatter */
function uiPhone(v) {
  if (!v) return 'N/A';
  var cleaned = String(v).replace(/\D/g, '');
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? (match[1] + '-' + match[2] + '-' + match[3]) : v;
}

const UserInfo = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(function onMount() {
    async function run() {
      try {
        const data = await fetchUserData(userId);
        setUser(data);
      } catch (err) {
        setError((err && err.message) ? err.message : 'Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    }
    run();
  }, [userId]);

  if (loading) {
    return (
      <div className="ui-userinfo-loading">
        <p className="ui-userinfo-loading-text">Loading user information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ui-userinfo-error">
        Error: {error}
        <button
          className="ui-userinfo-error-btn"
          onClick={function(){ navigate('/console/users'); }}
        >
          Back to List
        </button>
      </div>
    );
  }

  if (!user) return <div className="ui-userinfo-missing">User not found.</div>;

  function handlePetClick(member) {
    navigate('/console/petInfo/' + member.id);
    try { window.scrollTo(0, 0); } catch (e) {}
  }

  return (
    <div className="ui-userinfo-page">
      {/* Top bar */}
      <div className="ui-userinfo-topbar">
        <BackButton />
        <div className="ui-userinfo-actions">
          <button
            className="ui-btn ui-btn-pink"
            onClick={function(){ navigate('/console/userBookingInfo/' + userId); }}
          >
            Bookings
          </button>
          <button
            className="ui-btn ui-btn-purple"
            onClick={function(){ navigate('/console/userPaymentHistory/' + userId); }}
          >
            Transactions
          </button>
        </div>
      </div>

      {/* Profile Summary */}
      <section className="ui-card ui-userinfo-profile">
        <div className="ui-userinfo-avatar">
          {user.image
            ? <img src={user.image} alt={'Avatar of ' + uiVal(user.username, 'User')} />
            : <div className="ui-userinfo-avatar-fb">{(uiVal(user.username, 'U') + '').charAt(0)}</div>
          }
        </div>

        <div className="ui-userinfo-summary">
          <h1 className="ui-userinfo-title">{uiVal(user.username, 'User')}</h1>
          <div className="ui-userinfo-summary-grid">
            <div className="ui-kv">
              <span className="ui-kv-label">Email</span>
              <span className="ui-kv-value">{uiVal(user.email)}</span>
            </div>
            <div className="ui-kv">
              <span className="ui-kv-label">Phone</span>
              <span className="ui-kv-value">{uiPhone(user.phone)}</span>
            </div>
            <div className="ui-kv">
              <span className="ui-kv-label">Store</span>
              <span className="ui-kv-value">{uiVal(user.storeLocation)}</span>
            </div>
            <div className="ui-kv">
              <span className="ui-kv-label">Referral</span>
              <span className="ui-kv-value">{uiVal(user.referralCode)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <div className="ui-userinfo-sections">

        {/* Family Members */}
        {user.FamilyMembers && user.FamilyMembers.length > 0 && (
          <section className="ui-card">
            <h2 className="ui-section-title">Family Members</h2>
            <div className="ui-family-grid">
              {user.FamilyMembers.map(function(member){
                return (
                  <div
                    key={member.id}
                    className="ui-family-card"
                    onClick={function(){ handlePetClick(member); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={function(e){ if (e.key === 'Enter') handlePetClick(member); }}
                  >
                    <div className="ui-family-head">
                      <div className="ui-pet-thumb">
                        {member.image
                          ? <img src={member.image} alt={uiVal(member.name, 'Pet')} />
                          : <div className="ui-pet-thumb-fb">{(uiVal(member.name, 'P') + '').charAt(0)}</div>
                        }
                      </div>
                      <div className="ui-pet-id">
                        <div className="ui-pet-name">{uiVal(member.name)}</div>
                        <div className="ui-badge ui-badge-secondary">{uiVal(member.type)}</div>
                      </div>
                    </div>

                    <div className="ui-family-details-grid">
                      {member.type !== 'Human' ? (
                        <React.Fragment>
                          <div className="ui-f-field">
                            <span className="ui-f-label">Breed</span>
                            <span className="ui-f-value">{uiVal(member.breed)}</span>
                          </div>
                          <div className="ui-f-field">
                            <span className="ui-f-label">Age</span>
                            <span className="ui-f-value">{uiVal(member.age)}</span>
                          </div>
                          <div className="ui-f-field">
                            <span className="ui-f-label">DOB</span>
                            <span className="ui-f-value">{uiPrettyDate(member.dob)}</span>
                          </div>
                          <div className="ui-f-field">
                            <span className="ui-f-label">Vax</span>
                            <span className="ui-f-value">
                              {member.vaccination
                                ? (<a className="ui-link" href={member.vaccination} target="_blank" rel="noopener noreferrer">View</a>)
                                : 'N/A'
                              }
                            </span>
                          </div>
                          <div className="ui-f-field">
                            <span className="ui-f-label">Daycare</span>
                            <span className={member.daycare ? 'ui-tag ui-tag-ok' : 'ui-tag ui-tag-no'}>
                              {member.daycare ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </React.Fragment>
                      ) : (
                        <div className="ui-f-field">
                          <span className="ui-f-label">Relationship</span>
                          <span className="ui-f-value">{uiVal(member.relationship)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Wallet */}
        {user.Wallet && (
          <section className="ui-card">
            <h2 className="ui-section-title">Wallet</h2>
            <div className="ui-kv-grid">
              <div className="ui-kv"><span className="ui-kv-label">Balance</span><span className="ui-kv-value">${uiVal(user.Wallet.balance, '0.00')}</span></div>
              <div className="ui-kv"><span className="ui-kv-label">Total Spent</span><span className="ui-kv-value">${uiVal(user.Wallet.totalSpent, '0.00')}</span></div>
            </div>
          </section>
        )}

        {/* Payment Methods */}
        {user.PaymentMethods && user.PaymentMethods.length > 0 && (
          <section className="ui-card">
            <h2 className="ui-section-title">Payment Methods</h2>
            <div className="ui-pm-grid">
              {user.PaymentMethods.map(function(pm){
                return (
                  <div className="ui-pm-card" key={pm.id}>
                    <div className="ui-pm-line"><span className="ui-kv-label">Type</span><span className="ui-kv-value">{uiVal(pm.cardType)}</span></div>
                    <div className="ui-pm-line"><span className="ui-kv-label">Number</span><span className="ui-kv-value">{uiVal(pm.cardNumber)}</span></div>
                    <div className="ui-pm-line"><span className="ui-kv-label">Name</span><span className="ui-kv-value">{uiVal(pm.cardholderName)}</span></div>
                    <div className="ui-pm-line"><span className="ui-kv-label">Expiry</span><span className="ui-kv-value">{uiVal(pm.expiryMonth)}/{uiVal(pm.expiryYear)}</span></div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default UserInfo;
